// Property edit page — loads the property, then hands it to
// PropertyWizard in "edit" mode. The wizard's image step gets the
// real property id and mounts the full image uploader.

import { useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/empty-state";
import { ApiError } from "@/lib/api-client";
import { localizeError } from "@/lib/errorMessages";
import {
  useChangePrice,
  useChangeStatus,
  useProperty,
  useUpdateInfo,
} from "@/features/property/api";
import { PropertyDetailSkeleton } from "@/features/property/components/property-detail-skeleton";
import { PropertyWizard } from "@/features/property/wizard/property-wizard";
import {
  draftFromProperty,
  draftToPatchRequest,
  type PropertyDraft,
} from "@/features/property/wizard/wizard-state";

function parseId(raw: string | undefined): number | undefined {
  if (raw === undefined) return undefined;
  const n = Number.parseInt(raw, 10);
  return Number.isNaN(n) || n <= 0 ? undefined : n;
}

export default function PropertyEditPage() {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const propertyId = useMemo(() => parseId(params.id), [params.id]);
  const query = useProperty(propertyId);
  const updateInfo = useUpdateInfo(propertyId ?? 0);
  const changePrice = useChangePrice(propertyId ?? 0);
  const changeStatus = useChangeStatus(propertyId ?? 0);
  const submitting =
    updateInfo.isPending || changePrice.isPending || changeStatus.isPending;

  if (propertyId === undefined) return <NotFound />;
  if (query.isPending) return <PropertyDetailSkeleton />;
  if (query.isError) {
    if (query.error instanceof ApiError && query.error.status === 404) {
      return <NotFound />;
    }
    return (
      <EmptyState
        title="Không thể tải bất động sản"
        description="Vui lòng thử lại."
        action={
          <Button variant="outline" onClick={() => void query.refetch()}>
            Thử lại
          </Button>
        }
      />
    );
  }

  const property = query.data;
  const initialDraft = draftFromProperty(
    property as unknown as Parameters<typeof draftFromProperty>[0],
  );

  const handleSubmit = async ({ draft }: { draft: PropertyDraft }) => {
    // Edit flow uploads images via the live ImageUploader mounted in
    // step 3, so we ignore `pendingImages` from the wizard params here.
    try {
      // Info fields go through the parent PATCH. Price + status have their own
      // endpoints (each with its own audit action) and are NOT carried by the
      // info patch, so apply them here whenever the user changed them —
      // otherwise a price/status edit in the wizard would be silently dropped.
      await updateInfo.mutateAsync(draftToPatchRequest(draft));
      if (draft.price !== undefined && draft.price !== property.price) {
        await changePrice.mutateAsync(draft.price);
      }
      if (draft.status !== undefined && draft.status !== property.status) {
        await changeStatus.mutateAsync(draft.status);
      }
      toast.success("Đã cập nhật bất động sản");
      navigate(`/properties/${property.id}`);
    } catch (error) {
      toast.error(localizeError(error));
      throw error;
    }
  };

  return (
    <PropertyWizard
      mode="edit"
      initialDraft={initialDraft}
      propertyId={property.id}
      initialHeroImageUrl={property.thumbnailUrl ?? undefined}
      title={property.buildingName ?? "Bất động sản"}
      onSubmit={handleSubmit}
      submitting={submitting}
    />
  );
}

function NotFound() {
  return (
    <EmptyState
      title="Không tìm thấy bất động sản"
      description="Bất động sản này không còn tồn tại."
      action={
        <Button asChild variant="outline">
          <Link to="/properties">
            <ArrowLeft aria-hidden="true" />
            Quay lại danh sách
          </Link>
        </Button>
      }
    />
  );
}
